import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveOrg } from "@/hooks/useActiveOrg";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Save, Plus, X, Mail, User } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";

interface StudentEmail {
  email: string;
  name: string;
}

export default function InstructorCreateStudentList() {
  const { user } = useAuth();
  const { activeOrgId } = useActiveOrg();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    listName: "",
    description: ""
  });
  const [students, setStudents] = useState<StudentEmail[]>([{ email: "", name: "" }]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddStudent = () => {
    setStudents(prev => [...prev, { email: "", name: "" }]);
  };

  const handleRemoveStudent = (index: number) => {
    if (students.length > 1) {
      setStudents(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleStudentChange = (index: number, field: 'email' | 'name', value: string) => {
    setStudents(prev => prev.map((student, i) => 
      i === index ? { ...student, [field]: value } : student
    ));
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleCreateList = async () => {
    if (!user) return;

    // Validation
    if (!formData.listName.trim()) {
      toast.error('List name is required');
      return;
    }

    const validStudents = students.filter(s => s.email.trim() && s.name.trim());
    if (validStudents.length === 0) {
      toast.error('At least one student email and name is required');
      return;
    }

    const invalidEmails = validStudents.filter(s => !validateEmail(s.email));
    if (invalidEmails.length > 0) {
      toast.error('Please enter valid email addresses for all students');
      return;
    }

    setLoading(true);
    try {
      // Create the student list
      const { data: listData, error: listError } = await supabase
        .from('instructor_student_lists')
        .insert({
          instructor_id: user.id,
          org_id: activeOrgId ?? null,
          list_name: formData.listName.trim(),
          description: formData.description.trim() || null
        })
        .select()
        .single();

      if (listError) throw listError;

      // Add students to the list
      const studentEmails = validStudents.map(student => ({
        list_id: listData.id,
        email: student.email.trim().toLowerCase(),
        student_name: student.name.trim()
      }));

      const { error: emailError } = await supabase
        .from('instructor_student_emails')
        .insert(studentEmails);

      if (emailError) throw emailError;

      toast.success('Student list created successfully!');
      navigate('/instructor/students');
    } catch (error: any) {
      console.error('Error creating student list:', error);
      
      // Handle duplicate list name error
      if (error?.code === '23505' && error?.message?.includes('instructor_student_lists_instructor_id_list_name_key')) {
        toast.error('A student list with this name already exists. Please choose a different name.');
      } else {
        toast.error('Failed to create student list');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Create Student List
          </h1>
          <p className="text-muted-foreground">
            Create a list of students to invite to your tests and track their progress.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>List Details</CardTitle>
                <CardDescription>
                  Set up your student list with a name and description
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="listName">List Name *</Label>
                  <Input
                    id="listName"
                    placeholder="e.g., Grade 6 Math Class A"
                    value={formData.listName}
                    onChange={(e) => handleInputChange('listName', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Optional description for this student list..."
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Student Emails</CardTitle>
                    <CardDescription>
                      Add the email addresses and names of students you want to invite
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleAddStudent}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Student
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {students.map((student, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 border rounded-lg">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Student Name
                        </Label>
                        <Input
                          placeholder="Enter student name"
                          value={student.name}
                          onChange={(e) => handleStudentChange(index, 'name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email Address
                        </Label>
                        <Input
                          type="email"
                          placeholder="Enter email address"
                          value={student.email}
                          onChange={(e) => handleStudentChange(index, 'email', e.target.value)}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveStudent(index)}
                      disabled={students.length === 1}
                      className="mt-8"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <div className="text-sm text-muted-foreground">
                  <p>• Students will receive email invitations when you share tests</p>
                  <p>• Make sure to use valid email addresses</p>
                  <p>• You can add more students to this list later</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button 
                onClick={handleCreateList}
                disabled={loading}
                size="lg"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Creating...' : 'Create Student List'}
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                asChild
              >
                <Link to="/instructor/dashboard">Cancel</Link>
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Tips</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5 text-xs">1</Badge>
                  <p>Choose a descriptive name for your student list</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5 text-xs">2</Badge>
                  <p>Add student names and email addresses</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5 text-xs">3</Badge>
                  <p>Use this list to invite students to tests</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5 text-xs">4</Badge>
                  <p>Track student progress and results</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}